import db from "../db/database";
import { fetchFromBusinessCentral } from "../utils/businessCentralUtils";
import { mapApiOptionsResponse } from "../utils/syncUtils";
import { isOnline } from "./syncService";

/**
 * Mapa de endpoints -> tabla Dexie + endpoint
 */
const endpointConfig = {
  Departamentos: {
    table: "departamentos",
    path: "DepartamentosFE",
    label: "departamentos",
    paises: ["PY"],
  },
  Distritos: {
    table: "distritos",
    path: "DistritosFE",
    label: "distritos",
    paises: ["PY"],
  },
  Ciudades: {
    table: "ciudades",
    path: "CiudadesFE",
    label: "ciudades",
    paises: ["PY"],
  },
  Vendedores: {
    table: "vendedores",
    path: "Salesperson",
    label: "vendedores",
    // Se sincroniza por su propio camino (fetchVendedores), no por acá.
    enSincronizacionCompleta: false,
  },
  GrupoPrecios: {
    table: "grupoPrecios",
    path: "CustomerPriceGroup",
    label: "grupo de precios",
    paises: ["PY"],
  },
  GruposPrecioCliente: {
    table: "gruposPrecioCliente",
    path: "GruposPrecioCliente",
    label: "grupos de precio de cliente",
  },
  GruposDtoCliente: {
    table: "gruposDtoCliente",
    path: "GruposDtoCliente",
    label: "grupos de descuento de cliente",
    paises: ["PE"],
  },
  Puestos: {
    table: "puestos",
    path: "ListaPuestos",
    label: "puestos",
    // Forma propia, como GrupoCajeros: BC no devuelve Code/Description acá sino
    // { Tipo_registro, Codigo, Descripcion, Aplica_Jerarquia_Colegio }, así que
    // mapApiOptionsResponse no lo reconocería y el catálogo quedaría con
    // label/value vacíos. "Codigo" es lo que se guarda en el registro (ej. el
    // Cargo de un colegio viene como "JEF") y "Descripcion" es lo que se
    // muestra ("JEFE").
    //
    // Hay un puesto con Descripcion vacía (el código "17"): se cae al propio
    // código como etiqueta para que no aparezca una opción en blanco.
    mapFn: (item) => ({
      value: item.Codigo || null,
      label: item.Descripcion || item.Codigo || "",
      tipoRegistro: item.Tipo_registro || "",
      aplicaJerarquiaColegio: !!item.Aplica_Jerarquia_Colegio,
    }),
  },
  GrupoCajeros: {
    table: "grupoCajeros",
    path: "GrupoCajeros",
    label: "grupos de cajero",
    // Forma propia, distinta al resto de catálogos de este archivo -- BC no
    // devuelve Code/Description acá, sino { Tienda, Grupo, Descripcion,
    // Cliente_al_contado } (confirmado en vivo). "Grupo" es el código real
    // que se guarda en el usuario (users.Grupo) y "Descripcion" (sin tilde)
    // su descripción; mapApiOptionsResponse no los reconoce (busca "Code"/
    // "Descripción" con tilde), así que sin este mapFn el catálogo sincroniza
    // "con éxito" pero con label/value vacíos -- que es exactamente el bug
    // reportado ("no se ve el Grupo de cajero" al editar un usuario). Se
    // guarda también "tienda" por si en el futuro hace falta filtrar el
    // combo de Grupo según la Tienda seleccionada, o para la restricción de
    // cajas por grupo (ver comentario en AperturaDeCajaForm.js).
    mapFn: (item) => ({
      value: item.Grupo || null,
      label: item.Descripcion || item.Grupo || "",
      tienda: item.Tienda || "",
    }),
  },
};

/**
 * Fetch genérico de opciones para el componente select (antd) y guardado en la tabla correcta,
 */
// Forma con la que el resto de la aplicación conoce un catálogo.
const describirCatalogo = ([endpoint, config]) => ({
  endpoint,
  table: config.table,
  // El label es lo que se lee en los mensajes de sincronización; si algún
  // catálogo nuevo se olvidara de declararlo, se cae a su tabla antes que dejar
  // el mensaje a medias.
  label: config.label || config.table,
});

/**
 * Todos los catálogos declarados, sin filtrar por país.
 *
 * Es lo que necesita el mapa de sincronización individual: si la tarjeta de un
 * catálogo está en pantalla, su botón debe funcionar aunque ese catálogo no
 * entre en la sincronización completa de este país.
 */
export const todosLosCatalogosDeSeleccion = () =>
  Object.entries(endpointConfig).map(describirCatalogo);

/**
 * Los catálogos que `syncAll` debe recorrer en un país.
 *
 * Antes esta lista estaba escrita a mano DOS veces en syncService --una en el
 * bloque paralelo y otra en el mapa de sincronización individual-- y ya habían
 * divergido: Vendedores aparecía sólo en la segunda. Agregar un catálogo
 * requería tocar cinco sitios y acordarse de los dos; ahora se declara acá y las
 * dos listas salen solas.
 *
 * Se excluyen los marcados con `enSincronizacionCompleta: false` --los que ya
 * viajan por su propio camino-- y los de otros países.
 *
 * @param {string} [codigoPais] - Sin él se devuelven sólo los que aplican a todos.
 * @returns {Array<{endpoint: string, table: string, label: string}>}
 */
export const catalogosDeSeleccion = (codigoPais) =>
  Object.entries(endpointConfig)
    .filter(([, config]) => config.enSincronizacionCompleta !== false)
    .filter(([, config]) => !config.paises || config.paises.includes(codigoPais))
    .map(describirCatalogo);

export const fetchSelectOptions = async (endpoint) => {
  if (!isOnline()) {
    return false;
  }

  try {
    const cfg = endpointConfig[endpoint];
    if (!cfg) {
      console.error(`No hay configuración para endpoint '${endpoint}'.`);
      return false;
    }
    const { table, path, mapFn } = cfg;

    if (!db[table]) {
      console.error(`La tabla Dexie '${table}' no existe.`);
      return false;
    }

    await db[table].clear();

    const options = await fetchFromBusinessCentral(path);
    const selectOptions = mapFn ? options.map(mapFn) : mapApiOptionsResponse(options);
    const now = new Date();

    const records = selectOptions.map((opt) => ({
      ...opt,
      lastModified: now,
      lastSynced: now,
    }));

    await db[table].bulkAdd(records);
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Construye opciones listas para usar en Select de Ant Design
 */
export const buildSelectOptions = async (endpoint) => {
  try {
    const cfg = endpointConfig[endpoint];
    if (!cfg) {
      console.error(`No se encontró la configuración "${endpoint}"`);
      return [];
    }
    const { table } = cfg;

    // Si la tabla no existe en Dexie
    if (!db[table]) {
      console.error(`La tabla "${table}" no existe en la base de datos`);
      return [];
    }

    // Obtener registros actualizados desde Dexie
    const records = await db[table].toArray();

    return records;
  } catch (error) {
    console.error(`Error al construir opciones para ${endpoint}:`, error);
    return [];
  }
};
